/**
 * TripUpdatesPanel — shared widget for posting + viewing trip updates.
 *
 * Used by:
 *   - US transporter on `/transport/orders/:id` (kind=`us_transport`)
 *   - International courier on their dashboard (kind=`intl_lead`)
 *   - Buyer on the same surfaces — same component, role auto-detected
 *     via `GET /api/me/trip/{kind}/{id}` response (`is_courier`, `is_buyer`).
 *
 * Safety guardrails (matching backend):
 *   - Photos uploaded via `/api/me/trip/upload-photo` which strips EXIF.
 *   - Free-text addresses are auto-redacted server-side (we just show
 *     a tip telling the courier to type city names, not street addresses).
 *   - Public share link can be revoked + regenerated by the buyer.
 *   - Private update toggle hides an update from the public share link.
 */
import React, { useEffect, useRef, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapPinIcon as MapPin, CameraIcon as Camera, ShareIcon as Share, CheckCircleIcon as CheckCircle, ClockIcon as Clock, EyeSlashIcon as EyeSlash, ArrowsClockwiseIcon as ArrowsClockwise, WarningIcon as Warning } from "@phosphor-icons/react";

const STATUS_CHIPS = [
  { v: "pickup", label: "Picked up" },
  { v: "in_transit", label: "In transit" },
  { v: "rest_stop", label: "Rest stop" },
  { v: "border_crossing", label: "Crossing border" },
  { v: "customs", label: "Customs" },
  { v: "arrived", label: "Arrived" },
];

const MILESTONE = new Set(["pickup", "border_crossing", "customs", "arrived"]);

export default function TripUpdatesPanel({ tripKind, tripId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get(`/me/trip/${tripKind}/${tripId}`);
      setData(r.data);
    } catch (_) {
      setData({ updates: [], is_buyer: false, is_courier: false });
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tripKind, tripId]);

  if (!data) {
    return (
      <div className="h-32 rounded-xl bg-[#FBF7EC] animate-pulse" />
    );
  }

  return (
    <div data-testid={`trip-updates-${tripKind}-${tripId}`}>
      {data.is_courier && (
        <CourierPoster
          tripKind={tripKind}
          tripId={tripId}
          onPosted={reload}
          busy={busy}
          setBusy={setBusy}
        />
      )}
      {data.is_buyer && data.share_token && (
        <ShareBlock
          token={data.share_token}
          tripKind={tripKind}
          tripId={tripId}
          onRegen={reload}
        />
      )}
      <Timeline updates={data.updates || []} />
      <VenueFooter />
    </div>
  );
}

/* ============================================================
   Courier-side: post a new update
   ============================================================ */
function CourierPoster({ tripKind, tripId, onPosted, busy, setBusy }) {
  const [status, setStatus] = useState("in_transit");
  const [text, setText] = useState("");
  const [city, setCity] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const fileRef = useRef(null);

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post("/me/trip/upload-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(r.data?.photo_url || "");
      toast.success("Photo uploaded — EXIF data was stripped for safety.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/me/trip/updates", {
        trip_kind: tripKind,
        trip_id: tripId,
        status_chip: status,
        text,
        city,
        photo_url: photoUrl || null,
        is_private: isPrivate,
      });
      setText("");
      setCity("");
      setPhotoUrl("");
      setIsPrivate(false);
      toast.success(
        MILESTONE.has(status)
          ? "Posted &mdash; the buyer has been emailed."
          : "Update posted to the trip timeline."
      );
      onPosted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post update.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#E5D08A] rounded-xl p-5 mb-4"
      data-testid="trip-updates-courier-poster"
    >
      <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-3">
        Post an update
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.v}
            type="button"
            onClick={() => setStatus(c.v)}
            className={`text-[11px] h-7 px-2.5 rounded-full border ${
              status === c.v
                ? "bg-ink border-ink text-white"
                : "bg-white border-[#E5E5E5] text-[#444] hover:bg-[#FAFAFA]"
            }`}
            data-testid={`trip-status-${c.v}`}
          >
            {c.label}
            {MILESTONE.has(c.v) && (
              <span className="ml-1 text-[9px] text-[#8A6A00]">&bull; emails buyer</span>
            )}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <Input
          placeholder="City / airport (e.g. Frankfurt FRA)"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          data-testid="trip-update-city"
        />
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onPickPhoto}
            className="hidden"
            data-testid="trip-update-photo-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="rounded-full text-[12px] flex-1"
          >
            <Camera size={14} className="mr-1.5" />
            {photoUrl ? "Photo attached" : "Attach photo"}
          </Button>
        </div>
      </div>
      <Textarea
        placeholder="Update text (don't include street addresses &mdash; we'll redact them for safety)"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="mb-2"
        data-testid="trip-update-text"
      />
      <label className="flex items-center gap-2 text-[12px] text-[#5C5C5C] mb-3">
        <Checkbox
          checked={isPrivate}
          onCheckedChange={(v) => setIsPrivate(!!v)}
          data-testid="trip-update-private"
        />
        <EyeSlash size={14} weight="duotone" />
        <span>Private &mdash; buyer only, not visible on the share link.</span>
      </label>
      <Button
        onClick={submit}
        disabled={busy}
        className="rounded-full bg-crimson hover:bg-ink text-white"
        data-testid="trip-update-submit"
      >
        {busy ? "Posting&hellip;" : "Post update"}
      </Button>
      <p className="text-[10px] text-[#8B8B8B] mt-2 leading-relaxed">
        <Warning size={11} weight="duotone" className="inline mb-0.5 mr-1" />
        For your safety, post city names AFTER leaving each location. We
        never publish live GPS. EXIF metadata is stripped from every photo.
      </p>
    </div>
  );
}

/* ============================================================
   Buyer-side: share link block
   ============================================================ */
function ShareBlock({ token, tripKind, tripId, onRegen }) {
  const origin =
    typeof window !== "undefined" ? webOrigin() : "";
  const url = `${origin}/trip/${token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied. Forward it to family.");
    } catch {
      toast.error("Could not copy. Long-press and copy manually.");
    }
  };

  const revoke = async () => {
    if (!confirm("Revoke the share link? Anyone with the old link will get a 404."))
      return;
    await api.post("/me/trip/revoke-token", {
      trip_kind: tripKind, trip_id: tripId,
    });
    toast.info("Share link revoked.");
    onRegen();
  };

  const regen = async () => {
    await api.post("/me/trip/regen-token", {
      trip_kind: tripKind, trip_id: tripId,
    });
    toast.success("New share link minted.");
    onRegen();
  };

  return (
    <div
      className="bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4 mb-4 flex items-start gap-3"
      data-testid="trip-updates-share-block"
    >
      <Share size={20} weight="duotone" className="text-[#8A6A00] mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase">
          Share with family
        </div>
        <div className="font-mono text-[11px] text-ink mt-1 break-all">
          {url}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full text-[11px] h-7"
            onClick={copy}
            data-testid="trip-share-copy"
          >
            Copy link
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full text-[11px] h-7"
            onClick={regen}
            data-testid="trip-share-regen"
          >
            <ArrowsClockwise size={11} className="mr-1" />
            New link
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full text-[11px] h-7 text-crimson"
            onClick={revoke}
            data-testid="trip-share-revoke"
          >
            Revoke
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Timeline of past updates (both roles see this)
   ============================================================ */
function Timeline({ updates }) {
  if (updates.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#E5D08A] rounded-xl p-8 text-center">
        <Clock size={28} weight="duotone" className="text-[#8A6A00] mx-auto mb-2" />
        <p className="text-[13px] text-[#6B6B6B]">
          No updates yet. The courier will post check-ins as the trip progresses.
        </p>
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid="trip-updates-timeline">
      {updates.map((u) => (
        <li
          key={u.id}
          className={`bg-white border border-[#E5D08A] rounded-xl p-4 flex items-start gap-3 ${
            u.is_private ? "border-dashed" : ""
          }`}
          data-testid={`trip-update-row-${u.id}`}
        >
          <div className="w-9 h-9 rounded-full bg-[#FBF7EC] border border-[#E5D08A] flex items-center justify-center flex-shrink-0">
            {u.is_milestone ? (
              <CheckCircle size={18} weight="duotone" className="text-[#1A6E36]" />
            ) : (
              <MapPin size={18} weight="duotone" className="text-[#8A6A00]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[12px] font-semibold capitalize">
                {u.status_chip.replace("_", " ")}
              </span>
              {u.city && (
                <span className="text-[11px] text-[#6B6B6B]">
                  &middot; {u.city}
                </span>
              )}
              {u.is_private && (
                <span className="text-[10px] text-[#8A6A00] inline-flex items-center gap-1">
                  <EyeSlash size={10} /> Private
                </span>
              )}
            </div>
            {u.text && (
              <div className="text-[13px] text-ink mt-1 leading-relaxed whitespace-pre-wrap">
                {u.text}
              </div>
            )}
            {u.photo_url && (
              <img
                src={u.photo_url}
                alt="Trip update"
                className="mt-2 rounded-lg max-w-full max-h-72 object-cover"
              />
            )}
            <div className="text-[10px] text-[#8B8B8B] mt-1.5">
              {new Date(u.created_at).toLocaleString()}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function VenueFooter() {
  return (
    <div className="mt-4 text-[10px] text-[#8B8B8B] leading-relaxed">
      Updates are courier-submitted. Armstrong does not verify timing,
      location, or status &mdash; treat all ETAs as estimates.
    </div>
  );
}
